import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import {getToken} from '../lib/utils.js'
import {
    decodeJWTPayload,
    getTokenExpiration,
    getUserEmailFromToken,
    getUserIdFromToken,
    getUserPermissionsFromToken,
    getUserRolesFromToken,
    isTokenExpired
} from '@/lib/auth/jwt.js'
import { isSessionExpired } from '@/lib/auth/sessionGuard.js'

// SessionStorage key for caching token
const TOKEN_STORAGE_KEY = 'samform_auth_token'

// Token refresh interval: 5 minutes before expiration
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000

// Token loading timeout: 5 seconds
const TOKEN_LOAD_TIMEOUT_MS = 5000

export const useAuthStore = defineStore('auth', () => {
    // State
    const token = ref(null)
    const isLoading = ref(false)
    const error = ref(null)

    // Private state for concurrent call protection and auto-refresh
    let loadingPromise = null
    let refreshTimer = null

    // Recording-aware session state
    const isRecordingActive = ref(false)
    const sessionExpiredDuringRecording = ref(false)

    // Computed getters
    const isAuthenticated = computed(() => {
        return token.value && !isTokenExpired(token.value)
    })

    const user = computed(() => {
        if (!token.value) return null

        const payload = decodeJWTPayload(token.value)
        if (!payload) return null

        return {
            id: getUserIdFromToken(token.value),
            email: getUserEmailFromToken(token.value),
            name: payload.name || payload.given_name || payload.nickname,
            picture: payload.picture,
            roles: getUserRolesFromToken(token.value),
            permissions: getUserPermissionsFromToken(token.value),
            tokenExpiration: getTokenExpiration(token.value)
        }
    })

    const roles = computed(() => {
        return user.value?.roles || []
    })

    const permissions = computed(() => {
        return user.value?.permissions || []
    })

    // Actions

    /**
     * Load token from cache or API with timeout and concurrent call protection
     * Automatically caches token in sessionStorage and starts refresh timer
     * Always validates against server to detect user changes after logout/login
     */
    async function loadToken() {
        // Session is already expired — redirect is in progress, no point in fetching
        if (isSessionExpired()) {
            isLoading.value = false
            return
        }

        // Return existing promise if already loading (concurrent call protection)
        if (loadingPromise) {
            return loadingPromise
        }

        isLoading.value = true
        error.value = null

        loadingPromise = (async () => {
            try {
                // Always fetch fresh token from API to detect user changes
                // Cache is only used as a backup if API call fails
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('Token load timeout')), TOKEN_LOAD_TIMEOUT_MS)
                })

                const tokenPromise = getToken()
                const tokenData = await Promise.race([tokenPromise, timeoutPromise])

                if (tokenData && !isTokenExpired(tokenData)) {
                    // Check if this is a different user than cached token
                    const cachedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY)
                    const cachedUserId = cachedToken ? getUserIdFromToken(cachedToken) : null
                    const newUserId = getUserIdFromToken(tokenData)

                    if (cachedUserId && newUserId && cachedUserId !== newUserId) {
                        // User changed - clear old cache
                        sessionStorage.removeItem(TOKEN_STORAGE_KEY)
                    }

                    token.value = tokenData
                    // Cache in sessionStorage
                    sessionStorage.setItem(TOKEN_STORAGE_KEY, tokenData)
                    // Start auto-refresh timer
                    startTokenRefreshTimer()
                } else {
                    token.value = null
                    sessionStorage.removeItem(TOKEN_STORAGE_KEY)
                    console.warn('Token is expired or invalid')
                }
            } catch (err) {
                error.value = err.message || 'Failed to load token'

                // Try to use cached token as fallback if API fails
                const cachedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY)
                if (cachedToken && !isTokenExpired(cachedToken)) {
                    console.warn('Using cached token as fallback after API error')
                    token.value = cachedToken
                    startTokenRefreshTimer()
                } else {
                    token.value = null
                    sessionStorage.removeItem(TOKEN_STORAGE_KEY)
                    console.error('Error loading token:', err)

                    // Log to Sentry with context
                    if (window.Sentry) {
                        window.Sentry.withScope((scope) => {
                            scope.setContext('auth', {
                                errorType: err.message === 'Token load timeout' ? 'timeout' : 'load_failed',
                                currentRoute: window.location.pathname,
                                userId: user.value?.id || 'unknown'
                            })
                            window.Sentry.captureException(err)
                        })
                    }

                    throw err
                }
            } finally {
                isLoading.value = false
                loadingPromise = null
            }
        })()

        return loadingPromise
    }

    /**
     * Start automatic token refresh timer
     * Refreshes token 5 minutes before expiration
     */
    function startTokenRefreshTimer() {
        // Clear existing timer
        if (refreshTimer) {
            clearTimeout(refreshTimer)
            refreshTimer = null
        }

        if (!token.value) return

        const expiration = getTokenExpiration(token.value)
        if (!expiration) return

        const now = Date.now()
        const expiresIn = expiration.getTime() - now
        const refreshIn = expiresIn - TOKEN_REFRESH_BUFFER_MS

        // Only set timer if token hasn't expired yet
        if (refreshIn > 0) {
            refreshTimer = setTimeout(async () => {
                console.log('Auto-refreshing token before expiration')
                try {
                    await loadToken()
                } catch (err) {
                    console.error('Auto-refresh failed:', err)
                }
            }, refreshIn)
        }
    }

    /**
     * Clear token from state, cache, and stop auto-refresh timer
     */
    function clearToken() {
        token.value = null
        error.value = null
        sessionStorage.removeItem(TOKEN_STORAGE_KEY)

        // Stop refresh timer
        if (refreshTimer) {
            clearTimeout(refreshTimer)
            refreshTimer = null
        }
    }

    function setToken(tokenValue) {
        token.value = tokenValue
        error.value = null
    }

    // Permission checking functions
    function hasRole(role) {
        return roles.value.includes(role)
    }

    function hasAnyRole(roleList) {
        if (!Array.isArray(roleList)) return false
        return roleList.some(role => roles.value.includes(role))
    }

    function hasAllRoles(roleList) {
        if (!Array.isArray(roleList)) return false
        return roleList.every(role => roles.value.includes(role))
    }

    function hasPermission(permission) {
        return permissions.value.includes(permission)
    }

    function hasAnyPermission(permissionList) {
        if (!Array.isArray(permissionList)) return false
        return permissionList.some(permission => permissions.value.includes(permission))
    }

    function hasAllPermissions(permissionList) {
        if (!Array.isArray(permissionList)) return false
        return permissionList.every(permission => permissions.value.includes(permission))
    }

    // Refresh token if needed
    async function refreshTokenIfNeeded() {
        if (isSessionExpired()) return
        if (!token.value || isTokenExpired(token.value)) {
            await loadToken()
        }
    }

    /**
     * Ping the backend to keep the Spring Session alive.
     * Returns true if successful, false if the session is gone.
     */
    async function keepAlive() {
        if (isSessionExpired()) return false
        try {
            await loadToken()
            return !isSessionExpired()
        } catch {
            return false
        }
    }

    return {
        // State
        token,
        isLoading,
        error,
        isRecordingActive,
        sessionExpiredDuringRecording,

        // Getters
        isAuthenticated,
        user,
        roles,
        permissions,

        // Actions
        loadToken,
        clearToken,
        refreshTokenIfNeeded,
        setToken,
        keepAlive,

        // Permission checks
        hasRole,
        hasAnyRole,
        hasAllRoles,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions
    }
})

export default useAuthStore
